import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';

import { ImportItem } from '../models/import-item';
import { ImportJob } from '../models/import-job';
import { ImportJobDetail } from '../models/import-job-detail';

export class ImportApi {
    constructor(protected baseUrl: string) {}

    public getImports(params?: URLSearchParams): Promise<ApiResult<PaginatedResponse<ImportJob>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/imports`, { params }),
            response => {
                response.data.items = response.data.items.map(ImportJob.fromPlainObject);
                return response.data;
            }
        );
    }

    public delete(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/imports/${importId}`),
            () => null
        );
    }

    public abort(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/abort`),
            () => null
        );
    }

    public publishArticles(importId: number, articleIds: number[]): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/imports/${importId}/publish-items`, {
                    itemIds: articleIds,
                }),
            () => null
        );
    }

    public removeArticles(importId: number, articleIds: number[]): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/delete-items`, { itemIds: articleIds }),
            () => null
        );
    }

    public getImportDetails(importId: number): Promise<ApiResult<ImportJobDetail>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/imports/${importId}`),
            response => response.data
        );
    }

    public getImportItems(
        importId: number,
        params: URLSearchParams
    ): Promise<ApiResult<PaginatedResponse<ImportItem>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/imports/${importId}/items`, { params }),
            response => response.data
        );
    }

    public computeItemChanges(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/compute-item-changes`),
            () => null
        );
    }

    public retryFailed(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/retry-failed`),
            () => null
        );
    }

    public retryWarning(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/retry-warning`),
            () => null
        );
    }
    public publishAll(importId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/imports/${importId}/publish-all-items`),
            () => null
        );
    }

    public postImportDetailSettings({
        importId,
        shouldDeleteExisting,
        shouldSkipCategoryCreation,
        keepSpecialPrices,
        shouldOverwriteFiles,
        shouldNotifySupplier,
        publishDate,
    }: {
        importId: number;
        shouldDeleteExisting?: boolean;
        shouldSkipCategoryCreation?: boolean;
        keepSpecialPrices?: boolean;
        shouldOverwriteFiles?: boolean;
        shouldNotifySupplier?: boolean;
        publishDate?: string | null;
    }): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/imports/${importId}/settings`, {
                    shouldDeleteExisting,
                    shouldSkipCategoryCreation,
                    keepSpecialPrices,
                    shouldOverwriteFiles,
                    shouldNotifySupplier,
                    publishDate,
                }),
            () => null
        );
    }
}
